/**
 * V2 — auth-bus filter: signal-only payloads; peers revalidate platform `/me`.
 */
import type { CustomerAuthCrossTabMessage } from 'pi-kiosk-shared/crossTab';

const PLATFORM_CHROME_TENANT_CODE = '__platform__';

function isGlobalLogoutMessage(message: CustomerAuthCrossTabMessage): boolean {
  return message.type === 'logout' && message.scope === 'global';
}

function messageCarriesAuthJwt(message: CustomerAuthCrossTabMessage): boolean {
  const record = message as Record<string, unknown>;
  for (const key of ['accessToken', 'refreshToken', 'token', 'idToken'] as const) {
    const value = record[key];
    if (typeof value === 'string' && value.length > 0) {
      return true;
    }
  }
  return false;
}

export type HonorAuthBusOptions = {
  readonly hasSession?: boolean;
};

export function shouldHonorAuthBusMessage(
  message: CustomerAuthCrossTabMessage,
  urlTenantCode: string,
  options?: HonorAuthBusOptions,
): boolean {
  if (messageCarriesAuthJwt(message)) {
    return false;
  }
  if (isGlobalLogoutMessage(message)) {
    return true;
  }
  if (message.scope === 'platform') {
    return true;
  }

  const hasSession = options?.hasSession ?? false;
  const normalizedUrl = urlTenantCode.trim();

  // Platform identity login publishes tenantCode '__platform__'. Honor on any URL
  // (cookies are not tenant-bound) — including platform-chrome guests.
  if (
    (message.type === 'login' || message.type === 'session-refreshed') &&
    'tenantCode' in message &&
    message.tenantCode.trim() === PLATFORM_CHROME_TENANT_CODE
  ) {
    return true;
  }

  if (
    !hasSession &&
    normalizedUrl === PLATFORM_CHROME_TENANT_CODE &&
    (message.type === 'login' || message.type === 'session-refreshed') &&
    'tenantCode' in message
  ) {
    const msgTenant = message.tenantCode.trim();
    // Home-tenant login while browsing platform chrome (e.g. railway-cafe → /shops).
    return msgTenant.length > 0 && msgTenant !== PLATFORM_CHROME_TENANT_CODE;
  }

  if (!hasSession) {
    return 'tenantCode' in message && message.tenantCode === urlTenantCode;
  }

  return 'tenantCode' in message && message.tenantCode === urlTenantCode;
}
